import React from "react";

// Shared, dependency-free inline renderer for chat message text. Supports a
// small, safe subset of markdown — **bold**, *italic* / _italic_, `code`, and
// links ([label](url) or bare URLs) — plus @mention highlighting.
//
// XSS-safe by construction: every node is built from React elements and text
// nodes (never dangerouslySetInnerHTML), so message content can't inject
// markup, and link hrefs are restricted to http(s). Generalizes the inline
// **bold** idea from the community BotChat into a reusable component.

// One pass matches, in priority order: markdown link, inline code, bold,
// italic (* or _), bare http(s) URL, bare www URL. A single capturing group
// makes String.split place matched tokens at odd indices, plain text at even.
const INLINE =
  /(\[[^\]]+?\]\((?:https?:\/\/|www\.)[^\s)]+\)|`[^`]+?`|\*\*[^*]+?\*\*|\*[^*\n]+?\*|_[^_\n]+?_|https?:\/\/[^\s]+|www\.[^\s]+)/g;

// First bare http(s) URL in a message, with trailing sentence punctuation
// peeled off (same treatment renderToken gives bare URLs). Used for unfurling.
export const firstHttpUrl = (content) => {
  const m = (content || "").match(/https?:\/\/[^\s]+/);
  if (!m) return null;
  return m[0].replace(/[.,!?;:]+$/, "");
};

const LINK_CLASS = "custom-link text-theme-buttonEnable";
const CODE_CLASS =
  "rounded bg-theme-chatDivider px-1 py-0.5 font-mono text-[0.85em]";

// Keep only http(s) links; www.* is upgraded to https. Anything else (e.g. a
// javascript: scheme) returns null and the token is rendered as plain text.
const toSafeHref = (url) => {
  const u = (url || "").trim();
  if (/^https?:\/\//i.test(u)) return u;
  if (/^www\./i.test(u)) return `https://${u}`;
  return null;
};

const renderLink = (href, label, key) => {
  const safe = toSafeHref(href);
  if (!safe) return label; // unsafe scheme -> show the raw text
  return (
    <a
      key={key}
      href={safe}
      target="_blank"
      rel="noopener noreferrer"
      className={LINK_CLASS}
    >
      {label}
    </a>
  );
};

const MessageContent = ({ content, className, mentions, onMentionClick }) => {
  const mentionNames = React.useMemo(
    () =>
      new Set(
        (mentions || [])
          .map((m) => (m?.username || "").toLowerCase())
          .filter(Boolean)
      ),
    [mentions]
  );

  // Plain text stays a React text node (auto-escaped). Highlight "@username"
  // only when it maps to a real mentioned member and a click handler exists.
  const renderPlain = (text, keyPrefix) => {
    if (!text) return null;
    if (mentionNames.size === 0 || !onMentionClick) return text;
    return text.split(/(@[a-zA-Z0-9_.]+)/g).map((part, j) => {
      const uname = part[0] === "@" ? part.slice(1) : null;
      if (uname && mentionNames.has(uname.toLowerCase())) {
        return (
          <span
            key={`${keyPrefix}-${j}`}
            className="cursor-pointer text-sky-400 hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              onMentionClick(uname);
            }}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const renderToken = (token, key) => {
    if (token.length >= 2 && token[0] === "`" && token.endsWith("`")) {
      return (
        <code key={key} className={CODE_CLASS}>
          {token.slice(1, -1)}
        </code>
      );
    }
    if (token.startsWith("**") && token.endsWith("**")) {
      return (
        <strong key={key} className="font-semibold">
          {token.slice(2, -2)}
        </strong>
      );
    }
    if (
      (token[0] === "*" && token.endsWith("*")) ||
      (token[0] === "_" && token.endsWith("_"))
    ) {
      return (
        <em key={key} className="italic">
          {token.slice(1, -1)}
        </em>
      );
    }
    if (token[0] === "[") {
      const m = token.match(/^\[([^\]]+)\]\((.+)\)$/);
      if (m) return renderLink(m[2], m[1], key);
    }
    // Bare URL: peel trailing sentence punctuation off the link.
    let url = token;
    let trail = "";
    const tm = url.match(/[.,!?;:]+$/);
    if (tm) {
      trail = tm[0];
      url = url.slice(0, -trail.length);
    }
    return (
      <React.Fragment key={key}>
        {renderLink(url, url)}
        {trail}
      </React.Fragment>
    );
  };

  const nodes = (content || "").split(INLINE).map((piece, i) => {
    if (!piece) return null;
    return i % 2 === 1 ? (
      renderToken(piece, i)
    ) : (
      <React.Fragment key={i}>{renderPlain(piece, i)}</React.Fragment>
    );
  });

  return <p className={className}>{nodes}</p>;
};

export default MessageContent;
